import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, FormFeedback, Row, Col, Button } from 'reactstrap';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';

const LicensePlateDataModal = ({ isOpen, toggle, mode, selectedLicensePlate, refreshData, customerId, insuranceCompanies }) => {
    const initialFormState = {
        licensePlateNumber: '',
        licensePlateStatus: '',
        carchassisNumber: '',
        isThereLovClaim: false,
        hasCarSoldBefore: false,
        carBrand: '',
        carModel: '',
        carYear: '',
        carOwner: '',
        description: '',
        trafficInsurancePolicy: {
            trafficInsuranceProvider: null,
            trafficInsuranceNumber: '',
            trafficInsuranceStart: '',
            trafficInsuranceExpiry: ''
        },
        cascoInsurancePolicy: {
            cascoInsuranceProvider: null,
            cascoInsuranceNumber: '',
            cascoInsuranceStart: '',
            cascoInsuranceExpiry: ''
        },
        customer: customerId
    };

    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        if (isOpen) {
            if (mode === 'modify' && selectedLicensePlate) {
                const trafficProvider = selectedLicensePlate.trafficInsurancePolicy?.trafficInsuranceProvider || null;
                const cascoProvider = selectedLicensePlate.cascoInsurancePolicy?.cascoInsuranceProvider || null;

                const formattedTrafficStart = selectedLicensePlate.trafficInsurancePolicy?.trafficInsuranceStart
                    ? selectedLicensePlate.trafficInsurancePolicy.trafficInsuranceStart.split('T')[0]
                    : '';
                const formattedTrafficExpiry = selectedLicensePlate.trafficInsurancePolicy?.trafficInsuranceExpiry
                    ? selectedLicensePlate.trafficInsurancePolicy.trafficInsuranceExpiry.split('T')[0]
                    : '';
                const formattedCascoStart = selectedLicensePlate.cascoInsurancePolicy?.cascoInsuranceStart
                    ? selectedLicensePlate.cascoInsurancePolicy.cascoInsuranceStart.split('T')[0]
                    : '';
                const formattedCascoExpiry = selectedLicensePlate.cascoInsurancePolicy?.cascoInsuranceExpiry
                    ? selectedLicensePlate.cascoInsurancePolicy.cascoInsuranceExpiry.split('T')[0]
                    : '';

                setFormData({
                    ...initialFormState,
                    ...selectedLicensePlate,
                    customer: customerId ? customerId : null,
                    trafficInsurancePolicy: {
                        ...initialFormState.trafficInsurancePolicy,
                        ...selectedLicensePlate.trafficInsurancePolicy,
                        trafficInsuranceProvider: trafficProvider,
                        trafficInsuranceStart: formattedTrafficStart,
                        trafficInsuranceExpiry: formattedTrafficExpiry
                    },
                    cascoInsurancePolicy: {
                        ...initialFormState.cascoInsurancePolicy,
                        ...selectedLicensePlate.cascoInsurancePolicy,
                        cascoInsuranceProvider: cascoProvider,
                        cascoInsuranceStart: formattedCascoStart,
                        cascoInsuranceExpiry: formattedCascoExpiry
                    },
                });
            } else {
                setFormData({
                    ...initialFormState,
                    customer: customerId ? customerId : null
                });
                setErrors({});
            }
        }
    }, [mode, selectedLicensePlate, customerId, isOpen]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        let processedValue = value;

        const fieldsToUppercase = [
            'licensePlateNumber', 'carchassisNumber', 'carBrand', 'carModel', 'carOwner', 'description'
        ];

        if (fieldsToUppercase.includes(name) && typeof value === 'string') {
            processedValue = value.toLocaleUpperCase('tr-TR');
        }
        if (name === 'licensePlateNumber') {
            processedValue = processedValue.replace(/\s+/g, '');
        }
        if (name === 'carchassisNumber') {
            processedValue = processedValue.replace(/[^A-Z0-9]/gi, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleTrafficInsuranceNumberChange = (e) => {
        const { value } = e.target;
        const processedValue = typeof value === 'string' ? value.toLocaleUpperCase('tr-TR') : value;
        setFormData(prev => ({
            ...prev,
            trafficInsurancePolicy: {
                ...prev.trafficInsurancePolicy,
                trafficInsuranceNumber: processedValue
            }
        }));
    };

    const handleCascoInsuranceNumberChange = (e) => {
        const { value } = e.target;
        const processedValue = typeof value === 'string' ? value.toLocaleUpperCase('tr-TR') : value;
        setFormData(prev => ({
            ...prev,
            cascoInsurancePolicy: {
                ...prev.cascoInsurancePolicy,
                cascoInsuranceNumber: processedValue
            }
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        const plateRegex = /^\d{2}[A-Z]{1,3}\d{2,4}$/i;
        const chassisRegex = /^[A-HJ-NPR-Z0-9]{17}$/i;

        if (!formData.licensePlateNumber?.trim()) {
            newErrors.licensePlateNumber = 'Plaka numarası zorunludur';
        }
        if (!formData.licensePlateStatus) {
            newErrors.licensePlateStatus = 'Plaka durumu zorunludur';
        }
        if (formData.carchassisNumber?.trim() && !chassisRegex.test(formData.carchassisNumber)) {
            newErrors.carchassisNumber = 'Geçerli bir 17 haneli şasi numarası girin (I, O, Q hariç).';
        }
        if (formData.carYear && !/^\d{4}$/.test(formData.carYear)) {
            newErrors.carYear = 'Yıl 4 haneli bir sayı olmalıdır.';
        } else if (formData.carYear && (parseInt(formData.carYear) < 1900 || parseInt(formData.carYear) > new Date().getFullYear() + 1)) {
            newErrors.carYear = 'Geçerli bir yıl girin.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast.error('Lütfen formdaki hataları düzeltin.');
            return;
        }

        try {
            const getProviderId = (providerValue) => {
                if (!providerValue) return null;
                return typeof providerValue === 'object' ? providerValue.id : providerValue;
            };

            const formatIsoDate = (dateString) => {
                if (!dateString || !/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;
                try {
                    return new Date(dateString).toISOString();
                } catch (e) {
                    return null;
                }
            };

            const trafficProviderId = getProviderId(formData.trafficInsurancePolicy?.trafficInsuranceProvider);
            const trafficInsurancePolicyPayload = trafficProviderId ? {
                trafficInsuranceProvider: [trafficProviderId],
                trafficInsuranceNumber: formData.trafficInsurancePolicy?.trafficInsuranceNumber || null,
                trafficInsuranceStart: formatIsoDate(formData.trafficInsurancePolicy?.trafficInsuranceStart),
                trafficInsuranceExpiry: formatIsoDate(formData.trafficInsurancePolicy?.trafficInsuranceExpiry)
            } : null;

            const cascoProviderId = getProviderId(formData.cascoInsurancePolicy?.cascoInsuranceProvider);
            const cascoInsurancePolicyPayload = cascoProviderId ? {
                cascoInsuranceProvider: [cascoProviderId],
                cascoInsuranceNumber: formData.cascoInsurancePolicy?.cascoInsuranceNumber || null,
                cascoInsuranceStart: formatIsoDate(formData.cascoInsurancePolicy?.cascoInsuranceStart),
                cascoInsuranceExpiry: formatIsoDate(formData.cascoInsurancePolicy?.cascoInsuranceExpiry)
            } : null;

            const payload = {
                customer: customerId || null, // String olarak gönder, array değil
                licensePlateNumber: formData.licensePlateNumber || "",
                licensePlateStatus: formData.licensePlateStatus || "",
                carchassisNumber: formData.carchassisNumber || null,
                isThereLovClaim: Boolean(formData.isThereLovClaim),
                hasCarSoldBefore: Boolean(formData.hasCarSoldBefore),
                carBrand: formData.carBrand || null,
                carModel: formData.carModel || null,
                carYear: formData.carYear ? formData.carYear.toString() : null,
                carOwner: formData.carOwner || null,
                description: formData.description || null,
                trafficInsurancePolicy: trafficInsurancePolicyPayload,
                cascoInsurancePolicy: cascoInsurancePolicyPayload
            };

            console.log('API\'ye gönderilen payload:', payload);

            const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/licenseplate';
            if (!apiEndpoint) {
                console.error('HATA: Plaka API endpoint tanımlanmamış (REACT_APP_LICENSEPLATE_API_URL)!');
                toast.error('API endpoint yapılandırması eksik.');
                return;
            }

            await handleAddModifyOperation({
                apiEndpoint: apiEndpoint,
                selectedItem: mode === 'modify' ? selectedLicensePlate : null,
                mode,
                data: payload,
                refreshDataCallback: refreshData,
                componentName: 'Plaka'
            });
            toggle();
        } catch (error) {
            console.error('İşlem hatası:', error);
        }
    };

    const handleTrafficInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const provider = providerId ? insuranceCompanies?.find(ic => ic.id === providerId) || providerId : null;

        setFormData(prev => ({
            ...prev,
            trafficInsurancePolicy: {
                ...prev.trafficInsurancePolicy,
                trafficInsuranceProvider: provider
            }
        }));
        
        if (errors.trafficInsurancePolicy?.trafficInsuranceProvider) {
            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceProvider: undefined }}));
        }
    };

    const handleCascoInsuranceProviderChange = (e) => {
        const providerId = e.target.value;
        const provider = providerId ? insuranceCompanies?.find(ic => ic.id === providerId) || providerId : null;

        setFormData(prev => ({
            ...prev,
            cascoInsurancePolicy: {
                ...prev.cascoInsurancePolicy,
                cascoInsuranceProvider: provider
            }
        }));
        
        if (errors.cascoInsurancePolicy?.cascoInsuranceProvider) {
            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceProvider: undefined }}));
        }
    };

    const getSelectedProviderId = (policy) => {
        if (!policy || !policy.trafficInsuranceProvider && !policy.cascoInsuranceProvider) return '';
        const provider = policy.trafficInsuranceProvider || policy.cascoInsuranceProvider;
        return typeof provider === 'object' && provider !== null ? provider.id : (provider || '');
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {mode === 'add' ? 'Plaka Ekle' : 'Plaka Düzenle'}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <FormGroup>
                        <Label for="licensePlateNumber">Plaka Numarası <span className="text-danger">*</span></Label>
                        <Input
                            id="licensePlateNumber"
                            name="licensePlateNumber"
                            value={formData.licensePlateNumber || ''}
                            onChange={handleInputChange}
                            placeholder="Örn: 34ABC123"
                            invalid={!!errors.licensePlateNumber}
                        />
                        <FormFeedback>{errors.licensePlateNumber}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="licensePlateStatus">Plaka Durumu <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="licensePlateStatus"
                            name="licensePlateStatus"
                            value={formData.licensePlateStatus || ''}
                            onChange={handleInputChange}
                            invalid={!!errors.licensePlateStatus}
                        >
                            <option value="">Seçiniz</option>
                            <option value="Mevcut Plaka">Mevcut Plaka</option>
                            <option value="Eskiden İşlem Görmüş Plaka">Eskiden İşlem Görmüş Plaka</option>
                            <option value="Eski Plaka">Eski Plaka</option>
                        </Input>
                        <FormFeedback>{errors.licensePlateStatus}</FormFeedback>
                    </FormGroup>

                    <FormGroup>
                        <Label for="carchassisNumber">Şasi Numarası</Label>
                        <Input
                            id="carchassisNumber"
                            name="carchassisNumber"
                            value={formData.carchassisNumber || ''}
                            onChange={handleInputChange}
                            placeholder="17 Haneli Şasi Numarası"
                            invalid={!!errors.carchassisNumber}
                        />
                        <FormFeedback>{errors.carchassisNumber}</FormFeedback>
                    </FormGroup>

                    <Row>
                        <Col md={6}>
                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label for="isThereLovClaim" className="me-3 mb-0"> Değer Kaybı Talebi?</Label>
                                <div className="d-flex">
                                    <div className="radio radio-success me-3 d-flex align-items-center">
                                        <Input
                                            id="isThereLovClaimTrue"
                                            type="radio"
                                            name="isThereLovClaim"
                                            checked={formData.isThereLovClaim === true}
                                            onChange={() => setFormData(prev => ({ ...prev, isThereLovClaim: true }))}
                                        />
                                        <Label htmlFor="isThereLovClaimTrue" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-danger d-flex align-items-center">
                                        <Input
                                            id="isThereLovClaimFalse"
                                            type="radio"
                                            name="isThereLovClaim"
                                            checked={formData.isThereLovClaim === false}
                                            onChange={() => setFormData(prev => ({ ...prev, isThereLovClaim: false }))}
                                        />
                                        <Label htmlFor="isThereLovClaimFalse" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup className="d-flex align-items-center mb-3">
                                <Label for="hasCarSoldBefore" className="me-3 mb-0">Araç Satıldı Mı?</Label>
                                <div className="d-flex">
                                    <div className="radio radio-success me-3 d-flex align-items-center">
                                        <Input
                                            id="hasCarSoldBeforeTrue"
                                            type="radio"
                                            name="hasCarSoldBefore"
                                            checked={formData.hasCarSoldBefore === true}
                                            onChange={() => setFormData(prev => ({ ...prev, hasCarSoldBefore: true }))}
                                        />
                                        <Label htmlFor="hasCarSoldBeforeTrue" className="ms-1 mb-0">Evet</Label>
                                    </div>
                                    <div className="radio radio-danger d-flex align-items-center">
                                        <Input
                                            id="hasCarSoldBeforeFalse"
                                            type="radio"
                                            name="hasCarSoldBefore"
                                            checked={formData.hasCarSoldBefore === false}
                                            onChange={() => setFormData(prev => ({ ...prev, hasCarSoldBefore: false }))}
                                        />
                                        <Label htmlFor="hasCarSoldBeforeFalse" className="ms-1 mb-0">Hayır</Label>
                                    </div>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carBrand">Araç Markası</Label>
                                <Input
                                    id="carBrand"
                                    name="carBrand"
                                    value={formData.carBrand || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: BMW"
                                    invalid={!!errors.carBrand}
                                />
                                <FormFeedback>{errors.carBrand}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carModel">Araç Modeli</Label>
                                <Input
                                    id="carModel"
                                    name="carModel"
                                    value={formData.carModel || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 320i"
                                    invalid={!!errors.carModel}
                                />
                                <FormFeedback>{errors.carModel}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carYear">Araç Yılı</Label>
                                <Input
                                    type="number"
                                    id="carYear"
                                    name="carYear"
                                    value={formData.carYear || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: 2020"
                                    invalid={!!errors.carYear}
                                />
                                <FormFeedback>{errors.carYear}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="carOwner">Araç Sahibi</Label>
                                <Input
                                    id="carOwner"
                                    name="carOwner"
                                    value={formData.carOwner || ''}
                                    onChange={handleInputChange}
                                    placeholder="Örn: Ahmet Yılmaz"
                                    invalid={!!errors.carOwner}
                                />
                                <FormFeedback>{errors.carOwner}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <h5>Trafik Sigortası</h5>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceProvider">Sağlayıcı</Label>
                                <Input
                                    type="select"
                                    id="trafficInsuranceProvider"
                                    name="trafficInsuranceProvider"
                                    value={getSelectedProviderId(formData.trafficInsurancePolicy)}
                                    onChange={handleTrafficInsuranceProviderChange}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceProvider}
                                >
                                    <option value="">Seçiniz</option>
                                    {insuranceCompanies?.map(company => (
                                        <option key={`trafik-${company.id}`} value={company.id}>{company.name}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceProvider}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceNumber">Poliçe No</Label>
                                <Input
                                    id="trafficInsuranceNumber"
                                    name="trafficInsuranceNumber"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceNumber || ''}
                                    onChange={handleTrafficInsuranceNumberChange}
                                    placeholder="Poliçe Numarası"
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceNumber}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceStart">Başlangıç Tarihi</Label>
                                <Input
                                    type="date"
                                    id="trafficInsuranceStart"
                                    name="trafficInsuranceStart"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceStart || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            trafficInsurancePolicy: {
                                                ...prev.trafficInsurancePolicy,
                                                trafficInsuranceStart: value
                                            }
                                        }));
                                        
                                        if (errors.trafficInsurancePolicy?.trafficInsuranceStart) {
                                            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceStart: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceStart}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceStart}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="trafficInsuranceExpiry">Bitiş Tarihi</Label>
                                <Input
                                    type="date"
                                    id="trafficInsuranceExpiry"
                                    name="trafficInsuranceExpiry"
                                    value={formData.trafficInsurancePolicy?.trafficInsuranceExpiry || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            trafficInsurancePolicy: {
                                                ...prev.trafficInsurancePolicy,
                                                trafficInsuranceExpiry: value
                                            }
                                        }));
                                        
                                        if (errors.trafficInsurancePolicy?.trafficInsuranceExpiry) {
                                            setErrors(prev => ({ ...prev, trafficInsurancePolicy: { ...prev.trafficInsurancePolicy, trafficInsuranceExpiry: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.trafficInsurancePolicy?.trafficInsuranceExpiry}
                                />
                                <FormFeedback>{errors.trafficInsurancePolicy?.trafficInsuranceExpiry}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <h5>Kasko Sigortası</h5>
                    <Row>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceProvider">Sağlayıcı</Label>
                                <Input
                                    type="select"
                                    id="cascoInsuranceProvider"
                                    name="cascoInsuranceProvider"
                                    value={getSelectedProviderId(formData.cascoInsurancePolicy)}
                                    onChange={handleCascoInsuranceProviderChange}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceProvider}
                                >
                                    <option value="">Seçiniz</option>
                                    {insuranceCompanies?.map(company => (
                                        <option key={`kasko-${company.id}`} value={company.id}>{company.name}</option>
                                    ))}
                                </Input>
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceProvider}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceNumber">Poliçe No</Label>
                                <Input
                                    id="cascoInsuranceNumber"
                                    name="cascoInsuranceNumber"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceNumber || ''}
                                    onChange={handleCascoInsuranceNumberChange}
                                    placeholder="Poliçe Numarası"
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceNumber}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceStart">Başlangıç Tarihi</Label>
                                <Input
                                    type="date"
                                    id="cascoInsuranceStart"
                                    name="cascoInsuranceStart"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceStart || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            cascoInsurancePolicy: {
                                                ...prev.cascoInsurancePolicy,
                                                cascoInsuranceStart: value
                                            }
                                        }));
                                        
                                        if (errors.cascoInsurancePolicy?.cascoInsuranceStart) {
                                            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceStart: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceStart}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceStart}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={3}>
                            <FormGroup>
                                <Label for="cascoInsuranceExpiry">Bitiş Tarihi</Label>
                                <Input
                                    type="date"
                                    id="cascoInsuranceExpiry"
                                    name="cascoInsuranceExpiry"
                                    value={formData.cascoInsurancePolicy?.cascoInsuranceExpiry || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setFormData(prev => ({
                                            ...prev,
                                            cascoInsurancePolicy: {
                                                ...prev.cascoInsurancePolicy,
                                                cascoInsuranceExpiry: value
                                            }
                                        }));
                                        
                                        if (errors.cascoInsurancePolicy?.cascoInsuranceExpiry) {
                                            setErrors(prev => ({ ...prev, cascoInsurancePolicy: { ...prev.cascoInsurancePolicy, cascoInsuranceExpiry: undefined }}));
                                        }
                                    }}
                                    invalid={!!errors.cascoInsurancePolicy?.cascoInsuranceExpiry}
                                />
                                <FormFeedback>{errors.cascoInsurancePolicy?.cascoInsuranceExpiry}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>

                    <hr className="mt-4 mb-4"/>
                    <FormGroup>
                        <Label for="description">Açıklama</Label>
                        <Input
                            type="textarea"
                            id="description"
                            name="description"
                            value={formData.description || ''}
                            onChange={handleInputChange}
                            placeholder="Açıklama giriniz..."
                            invalid={!!errors.description}
                        />
                        <FormFeedback>{errors.description}</FormFeedback>
                    </FormGroup>
                </Form>
            </ModalBody>
            <ModalFooter>
                <Button color="primary" onClick={handleSubmit}>
                    {mode === 'add' ? 'Ekle' : 'Güncelle'}
                </Button>
                <Button color="secondary" onClick={toggle} className="ms-2">
                    İptal
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default LicensePlateDataModal;
